import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ValidationError, AnySchema } from 'yup';

import { KeyboardKeys } from '../../assets/enums/keyboard-keys.enum';

import styles from './inline-editable-text.module.scss';

interface EditableTextProps {
  onSubmit: (value?: string) => void;
  value?: string;
  schema?: AnySchema;
  disabled?: boolean;
  isBorderless?: boolean;
  maxLength?: number;
  validationType?: 'onChange' | 'onSubmit';
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export const InlineEditableText: React.FC<EditableTextProps> = ({
  onSubmit,
  value,
  schema,
  disabled,
  maxLength,
  isBorderless,
  onClick,
  validationType = 'onSubmit',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [currentValue, setCurrentValue] = useState(value);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasError, setHasError] = useState<boolean>(false);

  const validate = useCallback(async () => {
    if (!schema) {
      return;
    }

    return schema
      .validate(currentValue, { abortEarly: false })
      .then((res) => {
        setHasError(false);
      })
      .catch((error: any) => {
        if (error instanceof ValidationError) {
          setHasError(true);
          throw new Error(error?.inner?.[0]?.message);
        }
      });
  }, [currentValue, schema]);

  useEffect(() => {
    if (validationType === 'onChange') {
      validate();
    }
  }, [currentValue, validate, validationType]);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const handleSubmit = async () => {
    /**
     * If the value is the same as the initial text, we don't submit
     * to avoid unnecessary API calls
     */
    if (!inputRef?.current || currentValue === value) {
      return;
    }

    try {
      await validate();
      await onSubmit(currentValue);
    } catch (e: any) {
      setErrorMessage(e.message);

      inputRef?.current?.focus();
    }
  };

  const handleBlur = async (e: any) => {
    setHasError(false);
    setErrorMessage(null);

    if (e.currentTarget.disabled) {
      return;
    }
    inputRef.current?.setAttribute('readOnly', 'true');

    await handleSubmit();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!inputRef?.current) {
      return;
    }

    if (e.key === KeyboardKeys.Escape) {
      setCurrentValue(value);
      setHasError(false);
      setErrorMessage(null);

      /**
       * Setting the disabled property to true will trigger the onblur event
       * and will not call the handleSubmit function
       * We re-enable it immediately after for further update
       */
      inputRef.current.disabled = true;
      inputRef.current.disabled = false;
    }

    if (e.key === KeyboardKeys.Enter) {
      inputRef.current.blur();
    }
  };

  const handleFocus = () => {
    if (!inputRef?.current) {
      return;
    }

    inputRef.current.removeAttribute('readOnly');
  };

  return (
    <div className={styles['container']} data-borderless={isBorderless} onClick={onClick}>
      <input
        className={styles['editable']}
        ref={inputRef}
        placeholder={'-'}
        value={currentValue}
        onChange={({ target }) => {
          setCurrentValue(target.value);
        }}
        onKeyDown={onKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        disabled={disabled}
        maxLength={maxLength}
        data-has-error={hasError}
        data-borderless={isBorderless}
        readOnly
      />

      {errorMessage && <div className={styles['error-tool-tip']}>{errorMessage}</div>}
    </div>
  );
};
